import { Box } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { getChatTargetKey, useChatDock } from "@components/chat-dock";
import { useSwitchActiveCompany } from "@components/shared/hooks/useSwitchActiveCompany";
import { ContactCenterThemeScope } from "@components/virtual-receptionist/theming/ContactCenterThemeScope";
import { playConfiguredMessageSound } from "src/notifications/utils/notificationSound";
import { useFloatingBubbles } from "../../context/floating-bubbles/FloatingBubblesContext";
import { useModals } from "../../context/ModalContext";
import { getModalKeysByStackOrder, saveNotificationReturnState } from "../../components/notifications/notificationReturnState";
import { ChatNotificationPayload, subscribeChatNotification } from "./chatNotificationBus";
import { ChatNotificationToast, ChatToast } from "./ChatNotificationToast";

const TOAST_DURATION = 15000;
const EXIT_DURATION = 320;
const MAX_TOASTS = 5;

export function NotificationCenter() {
  const location = useLocation();
  const [toasts, setToasts] = useState<ChatToast[]>([]);
  const [openingToastId, setOpeningToastId] = useState<string | null>(null);
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const { openChat, openChats, focusedKey, bumpUnread } = useChatDock();
  const { openCalls } = useFloatingBubbles();
  const { modals, showDialog } = useModals();
  const { switchActiveCompany } = useSwitchActiveCompany();
  const accountId = useSelector((state: any) => state.userState?.uid);
  const activeCompanyId = useSelector((state: any) => state.userState?.activeCompanyId ?? state.userState?.company?.id);

  const activeCompanyIdRef = useRef(activeCompanyId);
  activeCompanyIdRef.current = activeCompanyId;

  const dockStateRef = useRef({ openChats, focusedKey });
  dockStateRef.current = { openChats, focusedKey };

  const clearTimer = useCallback((id: string) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
  }, []);

  const closeToast = useCallback(
    (id: string) => {
      clearTimer(id);

      setToasts((current) => current.map((toast) => (toast.id === id ? { ...toast, isClosing: true } : toast)));
    },
    [clearTimer],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const handleOpenToast = useCallback(
    async (toast: ChatToast) => {
      const { target, companyId, companyName, chatName, title } = toast.payload;
      const needsCompanySwitch = companyId != null && String(companyId) !== String(activeCompanyIdRef.current);

      const openToastChat = () => {
        openCalls();
        openChat(target, {
          companyName: companyName ?? "",
          chatName: chatName ?? title,
          focusMessageUid: toast.payload.focusMessageUid ?? null,
          focusMessageText: toast.payload.focusMessageText ?? null,
        });

        closeToast(toast.id);
      };

      const switchCompanyAndOpen = async () => {
        setOpeningToastId(toast.id);

        try {
          saveNotificationReturnState({
            companyId: activeCompanyIdRef.current,
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
            modalKeys: getModalKeysByStackOrder(modals),
          });
          await switchActiveCompany(Number(companyId));
        } catch (error) {
          console.error("Error switching company to open notification:", error);
          setOpeningToastId(null);
          return;
        }

        setOpeningToastId(null);
        openToastChat();
      };

      if (needsCompanySwitch) {
        clearTimer(toast.id);

        showDialog({
          type: "confirm",
          title: "Switch company?",
          message: `You are about to switch to ${companyName || "another company"} to open this notification. Are you sure?`,
          confirmLabel: "Yes, switch",
          cancelLabel: "Cancel",
          onCancel: () => closeToast(toast.id),
          onConfirm: () => {
            void switchCompanyAndOpen();
          },
        });
        return;
      }

      openToastChat();
    },
    [openCalls, openChat, closeToast, clearTimer, location.hash, location.pathname, location.search, modals, showDialog, switchActiveCompany],
  );

  const showChatBrowserNotification = useCallback(
    (payload: ChatNotificationPayload) => {
      if (!("Notification" in window)) return;
      if (!window.isSecureContext) return;
      if (Notification.permission !== "granted") return;
      if (document.visibilityState === "visible") return;

      const title = payload.companyName ? `${payload.title} - ${payload.companyName}` : payload.title;
      const notification = new Notification(title, {
        body: payload.body || payload.chatName || "New message",
        icon: "/logo.png",
        badge: "/logo.png",
        tag: `${payload.kind}-${payload.id}`,
        data: {
          kind: payload.kind,
          companyId: payload.companyId ?? null,
          focusMessageUid: payload.focusMessageUid ?? null,
          target: payload.target,
        },
      });

      notification.onclick = () => {
        window.focus();
        void handleOpenToast({
          id: `${payload.id}-browser`,
          payload,
          isClosing: false,
        });
        notification.close();
      };
    },
    [handleOpenToast],
  );

  useEffect(() => {
    const unsubscribe = subscribeChatNotification((payload: ChatNotificationPayload) => {
      const chatKey = getChatTargetKey(payload.target);
      const { openChats: currentChats, focusedKey: currentFocus } = dockStateRef.current;
      const dockedChat = currentChats.find((chat) => chat.key === chatKey);
      const isPageVisible = document.visibilityState === "visible";

      showChatBrowserNotification(payload);

      if (isPageVisible && dockedChat && !dockedChat.minimized && currentFocus === chatKey) return;

      if (dockedChat) bumpUnread(chatKey);

      const id = `${payload.id}-${Date.now()}`;

      // Sonido del toast de mensajes.
      playConfiguredMessageSound(accountId);

      setToasts((current) => {
        const next = [{ id, payload, isClosing: false }, ...current];

        next.slice(MAX_TOASTS).forEach((dropped) => clearTimer(dropped.id));

        return next.slice(0, MAX_TOASTS);
      });

      timersRef.current[id] = setTimeout(() => closeToast(id), TOAST_DURATION);
    });

    return unsubscribe;
  }, [accountId, closeToast, clearTimer, bumpUnread, showChatBrowserNotification]);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  if (toasts.length === 0) return null;

  return (
    <ContactCenterThemeScope>
    <Box
      sx={{
        position: "fixed",
        top: 92,
        right: 20,
        zIndex: 1000001,
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        pointerEvents: "none",
        "& > *": { pointerEvents: "all" },
      }}
      role="region"
      aria-live="polite"
      aria-label="Message notifications"
      data-prevent-parent-trigger="true"
    >
      {toasts.map((toast) => (
        <ChatNotificationToast
          key={toast.id}
          toast={toast}
          durationMs={TOAST_DURATION}
          exitDurationMs={EXIT_DURATION}
          isOpening={openingToastId === toast.id}
          onOpen={handleOpenToast}
          onClose={closeToast}
          onExited={removeToast}
        />
      ))}
    </Box>
    </ContactCenterThemeScope>
  );
}
